import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/auth';
import { redirectService } from '../utils/redirect';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
    setLoading(false);
  }, []);

  const login = async (credentials) => {
    const userData = await authService.login(credentials);
    setUser(userData.user);
    return userData;
  };

  const register = async (userData) => {
    const result = await authService.register(userData);
    // DON'T set user here anymore since they need to verify email first
    return result;
  };

  const verifyEmail = async (userId, verificationCode) => {
    try {
      const response = await api.post('/auth/verify_email/', {
        user_id: userId,
        verification_code: verificationCode  // Changed from 'code' to 'verification_code'
      });
      
      // Store the auth token
      if (response.data.token) {
        localStorage.setItem('authToken', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      
      // Set user in context
      setUser(response.data.user);
      
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Helper function to handle post-auth redirects
  const handlePostAuthRedirect = () => {
    const intent = redirectService.getAndClearRedirectIntent();
    if (intent) {
      // Check if intent is recent (within 10 minutes)
      const isRecent = (Date.now() - intent.timestamp) < 10 * 60 * 1000;
      if (isRecent && intent.returnTo) {
        return intent;
      }
    }
    return null;
  };

  const resendVerification = async (userId) => {
    return await authService.resendVerification(userId);
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
  };
  const forgotPassword = async (email, turnstileToken) => {
    return await authService.forgotPassword(email, turnstileToken);
  };

  const verifyResetCode = async (userId, resetCode) => {
    return await authService.verifyResetCode(userId, resetCode);
  };

  const resetPassword = async (userId, resetCode, newPassword) => {
    const result = await authService.resetPassword(userId, resetCode, newPassword);
    setUser(result.user);
    return result;
  };

  const value = {
    user,
    login,
    register,
    verifyEmail,        // ADD THIS
    resendVerification, // ADD THIS
    forgotPassword,
    verifyResetCode,
    resetPassword,
    logout,
    handlePostAuthRedirect, // ADD THIS
    isAuthenticated: authService.isAuthenticated(),
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};